import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, PackageCheck, Scale, HeartHandshake } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../context/LangContext";
import { ProductCard } from "../components/shop/ProductCard";
import { CATEGORIES } from "../lib/i18n";
import { T, IntroLayer, FlyingBall, BreakBurst, CardFan, RevealContent } from "../components/shop/HeroIntro";
import { HeroSound } from "../components/shop/HeroSound";
import { LanguageTiles } from "../components/shop/PokemonMenu";
import { LaunchSection } from "../components/shop/Launch";

const STORY = "/founder-pn28.webp";

const Hero = () => {
  const ref = useRef(null);
  const [hh, setHh] = useState(131);
  useEffect(() => {
    const h = document.querySelector("[data-testid=site-header]");
    const ro = new ResizeObserver(() => setHh(h.offsetHeight));
    ro.observe(h);
    return () => ro.disconnect();
  }, []);
  const { scrollYProgress: p } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const shake = useTransform(p, [T.hit, T.hit + 0.006, T.hit + 0.012, T.hit + 0.018, T.hit + 0.024, T.hit + 0.03], [0, -12, 10, -6, 3, 0]);
  return (
    <section ref={ref} className="relative bg-[#120E1F] grain h-[340vh]" data-testid="hero-section" data-no-reveal>
      <div className="sticky overflow-hidden flex items-center" style={{ top: hh, height: `calc(100svh - ${hh}px)` }}>
        <div className="psychic-glow float absolute -top-40 -right-32 h-[640px] w-[640px] rounded-full" />
        <div className="volt-glow absolute -bottom-48 left-10 h-[520px] w-[520px] rounded-full" />
        <div className="accent-tri absolute bottom-0 inset-x-0 h-1" />
        <motion.div style={{ x: shake }} className="absolute inset-0 flex items-center">
          <IntroLayer p={p} />
          <FlyingBall p={p} />
          <CardFan p={p} />
          <BreakBurst p={p} />
          <RevealContent p={p} />
        </motion.div>
        <HeroSound p={p} />
      </div>
    </section>
  );
};

const Values = () => {
  const { t } = useLang();
  const items = [[ShieldCheck, "v1"], [PackageCheck, "v2"], [Scale, "v3"], [HeartHandshake, "v4"]];
  return (
    <section className="bg-white border-b border-[#120E1F]/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-2 lg:grid-cols-4 gap-8">
        {items.map(([Icon, k]) => (
          <div key={k} className="flex gap-4 items-start" data-testid={`value-${k}`}>
            <Icon className="h-6 w-6 text-[#F2C230] shrink-0" strokeWidth={1.5} />
            <div><p className="font-semibold text-sm text-[#120E1F]">{t(k)}</p><p className="text-xs text-[#707783] mt-1 leading-relaxed">{t(`${k}d`)}</p></div>
          </div>
        ))}
      </div>
    </section>
  );
};

const Categories = () => {
  const { t } = useLang();
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <h2 className="section-title mb-10">{t("categories")}</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {CATEGORIES.map((c, i) => (
          <Link key={c} to={`/shop?category=${c}`} data-testid={`home-cat-${c}`}
            className="group relative rounded-2xl border border-[#120E1F]/[0.07] bg-white p-6 sm:p-8 hover:border-[#F2C230] transition-colors overflow-hidden">
            <span className="font-mono text-xs text-[#F2C230]">0{i + 1}</span>
            <p className="font-display text-2xl sm:text-3xl text-[#120E1F] mt-6">{t("cat")[c]}</p>
            <p className="text-xs text-[#707783] mt-1">{t("cat_desc")[c]}</p>
            <ArrowRight className="absolute right-6 bottom-6 h-5 w-5 text-[#120E1F]/20 group-hover:text-[#F2C230] group-hover:translate-x-1 transition-[color,transform]" />
          </Link>
        ))}
      </div>
    </section>
  );
};

const Story = () => {
  const { t } = useLang();
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 grid lg:grid-cols-2 gap-12 items-center">
      <img src={STORY} alt="pokenewb28 – Gründer von Jolgen Cards" loading="lazy" data-testid="founder-image" className="rounded-[28px] aspect-square object-cover w-full ring-1 ring-[#8A5CD6]/40 shadow-[0_30px_60px_-25px_rgba(110,63,184,.6)]" />
      <div className="space-y-6">
        <p className="text-xs uppercase tracking-[0.25em] text-[#F2C230]">{t("story_kicker")}</p>
        <h2 className="font-display text-4xl sm:text-5xl text-[#120E1F] leading-tight">{t("story_title")}</h2>
        <p className="text-[#707783] leading-relaxed">{t("story_text")}</p>
        <Link to="/about" data-testid="home-story-link" className="inline-flex items-center gap-2 text-[#120E1F] font-semibold border-b-2 border-[#F2C230] pb-1 hover:gap-3 transition-[gap]">{t("read_more")} <ArrowRight className="h-4 w-4" /></Link>
      </div>
    </section>
  );
};

export default function Home() {
  const { t } = useLang();
  const [products, setProducts] = useState([]);
  useEffect(() => { api.get("/products", { params: { featured: true } }).then((r) => setProducts(r.data.slice(0, 4))); }, []);

  return (
    <div className="bg-[#F7F5FB]">
      <Hero />
      <Values />
      <LanguageTiles />
      <LaunchSection />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20">
        <div className="flex items-end justify-between mb-10 gap-4">
          <div><h2 className="section-title">{t("featured")}</h2><p className="text-[#707783] mt-2 text-sm">{t("featured_sub")}</p></div>
          <Link to="/shop" data-testid="home-view-all" className="text-sm font-semibold text-[#120E1F] hover:text-[#F2C230] whitespace-nowrap transition-colors">{t("view_all")} →</Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6" data-testid="featured-grid">
          {products.map((p, i) => <ProductCard key={p.id} p={p} i={i} />)}
        </div>
      </section>
      <Categories />
      <section className="bg-[#241A3A] grain relative overflow-hidden" data-testid="fair-banner">
        <div className="psychic-glow absolute -right-20 -top-20 h-96 w-96 rounded-full" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid md:grid-cols-2 gap-8 items-center">
          <h2 className="font-display text-4xl sm:text-5xl text-white leading-tight">{t("fair_title")}</h2>
          <p className="text-white/60 leading-relaxed text-lg">{t("fair_text")}</p>
        </div>
      </section>
      <div className="pt-24"><Story /></div>
    </div>
  );
}
